import React, { useState, useEffect } from 'react'
import Footer from '../components/Footer'
import { FaFacebookF } from 'react-icons/fa'
import { Link, useNavigate } from 'react-router-dom'
import { AiOutlineGoogle } from 'react-icons/ai'
import FadeLoader from 'react-spinners/FadeLoader'
import { useSelector, useDispatch } from 'react-redux'
import { customer_login, messageClear } from '../store/reducers/authReducer'
import toast from 'react-hot-toast'
import { mode } from '../utils/config'

const Login = () => {
  const { loader, successMessage, errorMessage, userInfo } = useSelector(
    (state) => state.auth
  )
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [state, setState] = useState({
    email: '',
    password: ''
  })

  // Handle changes in the input fields
  const inputHandle = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value
    })
  }

  // Handle login submission
  const login = (e) => {
    e.preventDefault()
    dispatch(customer_login(state))
  }

  // Check success/error messages & user login state
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage)
      dispatch(messageClear())
    }
    if (errorMessage) {
      toast.error(errorMessage)
      dispatch(messageClear())
    }
    if (userInfo) {
      navigate('/')
    }
  }, [successMessage, errorMessage, userInfo, navigate, dispatch])

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-gray-900 overflow-hidden">
      {/* Loader Overlay */}
      {loader && (
        <div className="fixed inset-0 z-[999] flex justify-center items-center bg-[#38303033]">
          <FadeLoader />
        </div>
      )}

      {/*
        ============ TOP WAVE SHAPE ============
        - Generated from https://getwaves.io/
        - Colors can be changed in <linearGradient>
      */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden leading-[0] rotate-180">
        <svg
          className="block w-[120%] min-w-[700px]"
          viewBox="0 0 1920 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="url(#gradientTop)"
            fillOpacity="0.6"
            d="M0,160L48,133.3C96,107,192,53,288,53.3C384,53,480,107,576,149.3C672,192,768,224,864,208C960,192,1056,128,1152,112C1248,96,1344,128,1440,128C1536,128,1632,96,1728,122.7C1824,149,1920,235,1920,235L1920,320L1824,320C1728,320,1632,320,1536,320C1440,320,1344,320,1248,320C1152,320,1056,320,960,320C864,320,768,320,672,320C576,320,480,320,384,320C288,320,192,320,96,320L0,320Z"
          />
          <defs>
            <linearGradient
              id="gradientTop"
              x1="0"
              y1="0"
              x2="0"
              y2="320"
              gradientUnits="userSpaceOnUse"
            >
              {/* Stop colors for top wave gradient */}
              <stop stopColor="#FFA585" />
              <stop offset="1" stopColor="#FFBB84" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/*
        ============ BOTTOM WAVE SHAPE ============
        - Generated from https://getwaves.io/
        - Colors can be changed in <linearGradient>
      */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-[0]">
        <svg
          className="block w-[120%] min-w-[700px]"
          viewBox="0 0 1920 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="url(#gradientBottom)"
            fillOpacity="0.6"
            d="M0,192L40,186.7C80,181,160,171,240,186.7C320,203,400,245,480,240C560,235,640,181,720,160C800,139,880,149,960,170.7C1040,192,1120,224,1200,256C1280,288,1360,320,1440,320C1520,320,1600,320,1680,298.7C1760,277,1840,235,1880,213.3L1920,192L1920,320L1880,320C1840,320,1760,320,1680,320C1600,320,1520,320,1440,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
          />
          <defs>
            <linearGradient
              id="gradientBottom"
              x1="0"
              y1="0"
              x2="0"
              y2="320"
              gradientUnits="userSpaceOnUse"
            >
              {/* Stop colors for bottom wave gradient */}
              <stop stopColor="#FFA585" />
              <stop offset="1" stopColor="#FFBB84" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Main Content (Glass/Neon Card) */}
      <div className="relative z-10 w-full max-w-md p-4 sm:p-8">
        {/* Neon/Glass Card */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl shadow-2xl p-6 sm:p-8 text-white">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              className="rounded-full w-24 h-24 sm:w-28 sm:h-28 object-cover shadow-md border-2 border-orange-300"
              src="/images/logo2.png"
              alt="Trend Street Logo"
            />
          </div>

          {/* Title */}
          <h2 className="text-center text-2xl sm:text-3xl font-bold mb-6 tracking-wide">
            Welcome Back
          </h2>

          {/* Form */}
          <form onSubmit={login} className="space-y-4">
            <div>
              <label htmlFor="email" className="block mb-1 font-semibold">
                Email
              </label>
              <input
                onChange={inputHandle}
                value={state.email}
                type="email"
                id="email"
                name="email"
                placeholder="Enter email"
                className="w-full px-3 py-2 rounded-md border border-orange-300 
                           bg-transparent focus:outline-none focus:ring-2 
                           focus:ring-orange-400 transition-colors"
              />
            </div>

            <div>
              <label htmlFor="password" className="block mb-1 font-semibold">
                Password
              </label>
              <input
                onChange={inputHandle}
                value={state.password}
                type="password"
                id="password"
                name="password"
                placeholder="Enter password"
                className="w-full px-3 py-2 rounded-md border border-orange-300 
                           bg-transparent focus:outline-none focus:ring-2 
                           focus:ring-orange-400 transition-colors"
              />
            </div>

            <button
              type="submit"
              className="w-full py-2 mt-4 rounded-md bg-orange-600 hover:bg-orange-700 
                         text-white font-semibold shadow-lg 
                         transition-all duration-200 outline-none"
            >
              Login
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-6">
            <hr className="flex-1 border-orange-300/50" />
            <span className="px-2 text-orange-200">OR</span>
            <hr className="flex-1 border-orange-300/50" />
          </div>

          {/* Social Buttons */}
          <button className="w-full py-2 mb-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white 
                             flex items-center justify-center gap-2 font-semibold shadow-md transition">
            <FaFacebookF />
            <span>Login with Facebook</span>
          </button>
          <button className="w-full py-2 rounded-md bg-red-600 hover:bg-red-700 text-white 
                             flex items-center justify-center gap-2 font-semibold shadow-md transition">
            <AiOutlineGoogle />
            <span>Login with Google</span>
          </button>

          {/* Register Links */}
          <div className="text-center mt-6 text-sm text-orange-200">
            <p>
              Don’t have an account?{' '}
              <Link className="text-orange-100 font-semibold hover:text-white" to="/register">
                Register
              </Link>
            </p>
            <p className="mt-2">
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-100 font-semibold hover:text-white"
                href={
                  mode === 'dev'
                    ? 'http://localhost:3001/register'
                    : 'https://hawkers-dashboard.vercel.app/register'
                }
              >
                Register as a Seller
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default Login
